import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, Button } from 'reactstrap';
import { toast } from 'react-toastify';
import { handleAddModifyOperation } from '../../../Api/ApiMethods'; // API metodunu import et

// Initial state for the form
const initialFormData = {
    title: '',
    text: '', // Internal state uses 'text'
    isActive: true, // Varsayılan olarak aktif
};

const AnnouncementDataModal = ({ apiEndpoint, isOpen, toggle, mode, selectedAnnouncement, refreshData }) => {
    const [formData, setFormData] = useState(initialFormData);
    const [loading, setLoading] = useState(false); // Loading state for the modal

    // Populate form when in modify mode or reset when modal opens for add mode
    useEffect(() => {
        if (isOpen) {
            if (mode === 'modify' && selectedAnnouncement) {
                // Populate form with selected announcement data
                setFormData({
                    title: selectedAnnouncement.title || '',
                    text: selectedAnnouncement.text || '',
                    // API'den gelen isActive değeri boolean olmalı
                    isActive: typeof selectedAnnouncement.isActive === 'boolean' ? selectedAnnouncement.isActive : true,
                });
            } else {
                // Reset form for add mode
                setFormData(initialFormData);
            }
        }
    }, [mode, selectedAnnouncement, isOpen]);

    const handleInputChange = (e) => {
        const { name, value, type } = e.target;

        // Başlangıç değeri radio için özel, diğerleri için 'value'
        let processedValue = value;

        // Büyük harfe çevrilecek alanlar
        const fieldsToUppercase = ['title', 'text'];

        if (type === 'radio' && name === 'isActive') {
             // Radio buton değeri zaten string "true" veya "false"
             // State'e boolean olarak kaydediyoruz
             processedValue = value === 'true';
             setFormData(prev => ({
                ...prev,
                isActive: processedValue
             }));
        } else {
             // Eğer alan listedeyse VE değer bir string ise Türkçe'ye özgü büyük harfe çevir
             if (fieldsToUppercase.includes(name) && typeof value === 'string') {
                 processedValue = value.toLocaleUpperCase('tr-TR');
             }
            // Diğer durumlarda değeri olduğu gibi al (örn: isActive boolean)

             setFormData(prev => ({
                ...prev,
                [name]: processedValue // İşlenmiş veya orijinal değeri state'e ata
             }));
        }
    };

    const resetForm = () => {
        setFormData(initialFormData);
    };

    const handleCancel = () => {
        resetForm();
        toggle(); // Close the modal
    };

    // Handle form submission (Add or Modify)
    const handleSubmit = async (e) => {
        e.preventDefault();
        // Basit zorunlu alan kontrolü
        if (!formData.title || !formData.text) {
            toast.error("Başlık ve Açıklama alanları zorunludur.");
            return;
        }

        setLoading(true);
        const apiData = {
            title: formData.title,
            text: formData.text,
            isActive: formData.isActive,
        };

        try {
            await handleAddModifyOperation({
                apiEndpoint,
                selectedItem: mode === 'modify' ? selectedAnnouncement : null, // Pass selected item only in modify mode
                mode,
                data: apiData,
                refreshDataCallback: () => {
                    if (refreshData) refreshData(); // Refresh the table data
                 },
                componentName: 'Duyuru', // For toast messages
            });
            handleCancel(); // Close modal on success
        } catch (error) {
            // Error is already handled in handleAddModifyOperation (toast message)
            console.error("Operation failed:", error);
        } finally {
            setLoading(false);
        }
    };

    // Determine modal title and button label based on mode
    const getModalTitle = () => (mode === 'add' ? 'Yeni Duyuru Ekle' : 'Duyuru Düzenle');
    const getButtonLabel = () => (mode === 'add' ? 'Ekle' : 'Güncelle');

    return (
        <Modal isOpen={isOpen} toggle={handleCancel} size="lg">
            <ModalHeader toggle={handleCancel}>{getModalTitle()}</ModalHeader>
            <Form onSubmit={handleSubmit}>
                <ModalBody>
                    <FormGroup>
                        <Label for="title">Başlık <span className="text-danger">*</span></Label>
                        <Input
                            type="text"
                            name="title"
                            id="title"
                            value={formData.title}
                            onChange={handleInputChange}
                            required
                            disabled={loading}
                            placeholder="Duyuru başlığını girin"
                        />
                    </FormGroup>
                    <FormGroup>
                        <Label for="text">Açıklama <span className="text-danger">*</span></Label>
                        <Input
                            type="textarea"
                            name="text"
                            id="text"
                            rows="4"
                            value={formData.text}
                            onChange={handleInputChange}
                            required
                            disabled={loading}
                             placeholder="Duyuru içeriğini girin"
                        />
                    </FormGroup>
                    {/* Checkbox yerine Radio Buton Grubu */}
                    <FormGroup className="d-flex align-items-center mb-3">
                         {/* Etiketi sola yasla */}
                        <Label className="me-3" style={{ minWidth: '100px' }}>Durum</Label>
                         {/* Radio butonları sağa yasla */}
                        <div className="d-flex">
                            <div className="radio radio-success me-3 d-flex align-items-center">
                                <Input
                                    id="isActiveTrue"
                                    type="radio"
                                    name="isActive"
                                    value="true" // Değeri string "true" olarak gönder
                                    checked={formData.isActive === true} // Kontrolü boolean ile yap
                                    onChange={handleInputChange} // handleInputChange içinden boolean'a çevirilecek
                                    disabled={loading}
                                />
                                {/* Label'a tıklanabilirlik için htmlFor ekle */}
                                <Label htmlFor="isActiveTrue" className="ms-1 mb-0">Aktif</Label>
                            </div>
                            <div className="radio radio-danger d-flex align-items-center">
                                <Input
                                    id="isActiveFalse"
                                    type="radio"
                                    name="isActive"
                                    value="false" // Değeri string "false" olarak gönder
                                    checked={formData.isActive === false} // Kontrolü boolean ile yap
                                    onChange={handleInputChange} // handleInputChange içinden boolean'a çevirilecek
                                    disabled={loading}
                                />
                                <Label htmlFor="isActiveFalse" className="ms-1 mb-0">Pasif</Label>
                            </div>
                        </div>
                    </FormGroup>
                </ModalBody>
                <ModalFooter>
                    <Button color="outline-primary" type="submit" disabled={loading}>
                        {loading ? 'Kaydediliyor...' : getButtonLabel()}
                    </Button>
                    <Button color="outline-danger" className="ms-2" onClick={handleCancel} disabled={loading}>
                        İptal
                    </Button>
                </ModalFooter>
            </Form>
        </Modal>
    );
};

export default AnnouncementDataModal; 